import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Clock, ChevronLeft, Loader2, CheckCircle2, Ticket } from "lucide-react";
import { useAppMode } from "@/hooks/useAppMode";
import { useServices } from "@/hooks/useServices";
import { useAvailableTimeSlots } from "@/hooks/useAvailableTimeSlots";
import { useUserMemberships } from "@/hooks/useUserMemberships";
import { useProviderWaivers } from "@/hooks/useProviderWaivers";
import { useUserWaiverAgreements } from "@/hooks/useWaiverAgreements";
import { useBookingState } from "@/hooks/useBookingState";
import { useBookingHandlers } from "@/hooks/useBookingHandlers";
import { useTimeSlotRealtime } from "@/hooks/useTimeSlotRealtime";
import { useActivePromotions } from "@/hooks/usePromotions";
import { useAvailableBookingCoupons, AvailableCoupon } from "@/hooks/useBookingCoupons";
import { useUserCouponCredits } from "@/hooks/useUserCouponCredits";
import WaiverForm from "@/components/booking/WaiverForm";
import { startOfDay, isBefore } from "date-fns";

const AppBookingFlow = () => {
  const { providerId, serviceId } = useParams();
  const navigate = useNavigate();
  const { provider } = useAppMode();
  const brandColor = provider?.brand_primary_color || undefined;

  const { data: services } = useServices(providerId);
  const selectedService = services?.find((s) => s.id === serviceId);

  const {
    selectedDate,
    setSelectedDate,
    selectedTimeSlot,
    setSelectedTimeSlot,
    selectedMembershipId,
    setSelectedMembershipId,
    processing,
    setProcessing,
    waiversCompleted,
    setWaiversCompleted,
  } = useBookingState();

  const [selectedCoupon, setSelectedCoupon] = useState<AvailableCoupon | null>(null);
  const timeSlotsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedDate && timeSlotsRef.current) {
      // Wait for time slots to render before scrolling
      const t = setTimeout(() => {
        timeSlotsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(t);
    }
  }, [selectedDate]);

  const { data: availableTimeSlots, isLoading: timeSlotsLoading } =
    useAvailableTimeSlots(serviceId, selectedDate, false);

  useTimeSlotRealtime(serviceId, selectedDate);

  const { data: userMemberships } = useUserMemberships(providerId, serviceId);
  const { data: waivers } = useProviderWaivers(providerId);
  const { data: existingAgreements } = useUserWaiverAgreements(providerId);
  const { data: availableCoupons } = useAvailableBookingCoupons(serviceId, providerId);

  const hasActiveWaivers = !!waivers && waivers.length > 0;
  const allWaiversAgreed = hasActiveWaivers
    ? waivers.every((w) =>
        existingAgreements?.some((a) => a.waiver_id === w.id)
      )
    : true;

  useEffect(() => {
    setWaiversCompleted(allWaiversAgreed);
  }, [allWaiversAgreed, setWaiversCompleted]);

  // Auto-select first usable membership if available
  const firstMembership = userMemberships?.[0];
  useEffect(() => {
    if (firstMembership && !selectedMembershipId) {
      setSelectedMembershipId(firstMembership.id);
    }
  }, [firstMembership, selectedMembershipId, setSelectedMembershipId]);

  // Active promotion for this service/date
  const dateString = selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined;
  const { data: promotions } = useActivePromotions(serviceId, dateString);
  const activePromotion = promotions && promotions.length > 0 ? promotions[0] : null;

  const originalPrice = selectedService?.price || 0;
  const promotionDiscount = activePromotion
    ? activePromotion.discount_type === "percentage"
      ? (originalPrice * (activePromotion.discount_percentage || 0)) / 100
      : Math.min(activePromotion.discount_amount || 0, originalPrice)
    : 0;
  const priceAfterPromotion = Math.max(0, originalPrice - promotionDiscount);

  // Coupon discount calculation (applied on top of promotion)
  const isCreditBasedCoupon =
    selectedCoupon?.coupon.discount_type === "weekly_credits" ||
    selectedCoupon?.coupon.discount_type === "monthly_credits";

  const calcCouponDiscount = (coupon: AvailableCoupon | null, price: number) => {
    if (!coupon) return 0;
    const { discount_type, discount_value } = coupon.coupon;
    if (discount_type === "weekly_credits" || discount_type === "monthly_credits") {
      return price; // free session
    }
    if (discount_type === "fixed") {
      return Math.min(discount_value, price);
    }
    return Math.min((price * discount_value) / 100, price);
  };

  const couponDiscount = calcCouponDiscount(selectedCoupon, priceAfterPromotion);
  const totalDiscount = promotionDiscount + couponDiscount;
  const priceAfterCoupon = Math.max(0, priceAfterPromotion - couponDiscount);
  const finalPrice = selectedMembershipId ? 0 : priceAfterCoupon;

  // Coupon clears membership and vice versa
  const handleCouponSelect = (coupon: AvailableCoupon | null) => {
    setSelectedCoupon(coupon);
    if (coupon) setSelectedMembershipId(null);
  };

  const { handleBookingWithMembership, handleRegularBooking, handleFreeBooking } =
    useBookingHandlers({
      providerId,
      serviceId,
      selectedDate,
      selectedTimeSlot,
      selectedMembershipId,
      selectedCoupon,
      availableTimeSlots,
      selectedService,
      userMemberships,
      hasActiveWaivers,
      waiversCompleted,
      couponDiscount: totalDiscount,
      isCreditBasedCoupon,
      setProcessing,
    });

  const canBook =
    !!selectedDate &&
    !!selectedTimeSlot &&
    waiversCompleted &&
    !processing;

  const handleBook = () => {
    if (selectedMembershipId) {
      handleBookingWithMembership();
    } else if (finalPrice <= 0) {
      handleFreeBooking();
    } else {
      handleRegularBooking();
    }
  };

  const selectedSlot = availableTimeSlots?.find(
    (s) => s.id === selectedTimeSlot
  );

  if (!selectedService) {
    return (
      <MobileAppLayout title="Loading...">
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MobileAppLayout>
    );
  }

  const ctaLabel = processing
    ? "Processing..."
    : selectedMembershipId
      ? "Confirm booking"
      : finalPrice <= 0
        ? "Confirm booking"
        : `Pay £${finalPrice.toFixed(2)}`;

  return (
    <MobileAppLayout title={selectedService.name}>
      <div className="px-4 py-4 space-y-4 pb-24">
        {/* Back */}
        <button
          onClick={() => navigate("/app/book")}
          className="flex items-center text-sm text-muted-foreground -ml-1"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to services
        </button>

        {/* Service summary */}
        <Card>
          <CardContent className="p-4">
            <h2 className="font-semibold text-lg mb-1">
              {selectedService.name}
            </h2>
            <div className="flex items-center text-sm text-muted-foreground gap-3">
              <span className="flex items-center">
                <Clock className="h-3.5 w-3.5 mr-1" />
                {selectedService.duration_minutes} min
              </span>
              {activePromotion ? (
                <span className="flex items-center gap-1.5">
                  <span className="line-through">£{originalPrice.toFixed(2)}</span>
                  <span className="font-semibold text-green-600">
                    £{priceAfterPromotion.toFixed(2)}
                  </span>
                </span>
              ) : (
                <span className="font-medium text-foreground">
                  £{originalPrice.toFixed(2)}
                </span>
              )}
            </div>
            {activePromotion && (
              <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-xs font-medium">
                Promotion · {activePromotion.name}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Membership badge */}
        {firstMembership && selectedMembershipId && (
          <Card className="bg-accent/50 border-accent">
            <CardContent className="p-3 flex items-center gap-2">
              <CheckCircle2
                className="h-4 w-4 flex-shrink-0"
                style={brandColor ? { color: brandColor } : undefined}
              />
              <p className="text-sm font-medium">
                Using your membership — no charge
              </p>
            </CardContent>
          </Card>
        )}

        {/* Available coupons */}
        {!selectedMembershipId && availableCoupons && availableCoupons.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2 px-1 flex items-center gap-1.5">
              <Ticket className="h-4 w-4" />
              Available coupons
            </h3>
            <div className="space-y-2">
              {availableCoupons.map((uc) => (
                <CouponRow
                  key={uc.id}
                  userCoupon={uc}
                  selectedDate={selectedDate}
                  servicePrice={priceAfterPromotion}
                  isSelected={selectedCoupon?.id === uc.id}
                  brandColor={brandColor}
                  onToggle={() =>
                    handleCouponSelect(selectedCoupon?.id === uc.id ? null : uc)
                  }
                />
              ))}
            </div>
          </div>
        )}

        {/* Waivers */}
        {hasActiveWaivers && !waiversCompleted && providerId && (
          <WaiverForm
            providerId={providerId}
            onAllWaiversAgreed={() => setWaiversCompleted(true)}
          />
        )}

        {/* Date picker */}
        <div>
          <h3 className="text-sm font-semibold mb-2 px-1">Select a date</h3>
          <Card>
            <CardContent className="p-2 flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  setSelectedDate(date);
                  setSelectedTimeSlot("");
                }}
                disabled={(date) =>
                  isBefore(startOfDay(date), startOfDay(new Date()))
                }
                className="rounded-md pointer-events-auto"
              />
            </CardContent>
          </Card>
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div ref={timeSlotsRef} className="scroll-mt-4">
            <h3 className="text-sm font-semibold mb-2 px-1">
              Available times
            </h3>
            <Card>
              <CardContent className="p-3">
                {timeSlotsLoading ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="h-12 bg-muted rounded animate-pulse"
                      />
                    ))}
                  </div>
                ) : !availableTimeSlots || availableTimeSlots.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    No available times for this date
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {availableTimeSlots.map((slot) => {
                      const isSelected = selectedTimeSlot === slot.id;
                      const spotsLeft =
                        slot.max_bookings - slot.current_bookings;
                      const disabled = spotsLeft <= 0;
                      const time = format(new Date(slot.start_time), "HH:mm");
                      return (
                        <button
                          key={slot.id}
                          disabled={disabled}
                          onClick={() => setSelectedTimeSlot(slot.id)}
                          className={`h-12 rounded-md border text-sm font-medium transition-colors ${
                            isSelected
                              ? "text-white border-transparent"
                              : disabled
                                ? "bg-muted text-muted-foreground border-muted opacity-60"
                                : "bg-background border-border hover:bg-accent"
                          }`}
                          style={
                            isSelected && brandColor
                              ? { backgroundColor: brandColor }
                              : undefined
                          }
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Summary */}
        {selectedDate && selectedTimeSlot && selectedSlot && (
          <Card>
            <CardContent className="p-4 space-y-2">
              <h3 className="font-semibold mb-1">Summary</h3>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Date</span>
                <span>{format(selectedDate, "EEE, MMM d")}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Time</span>
                <span>{format(new Date(selectedSlot.start_time), "HH:mm")}</span>
              </div>
              {!selectedMembershipId && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Service</span>
                    <span>£{originalPrice.toFixed(2)}</span>
                  </div>
                  {activePromotion && promotionDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Promotion ({activePromotion.name})</span>
                      <span>-£{promotionDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedCoupon && couponDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Coupon ({selectedCoupon.coupon.code})</span>
                      <span>-£{couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Total</span>
                <span
                  className={selectedMembershipId || finalPrice === 0 ? "text-green-600" : ""}
                >
                  £{finalPrice.toFixed(2)}
                </span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky CTA */}
      <div
        className="fixed bottom-16 left-0 right-0 bg-background/95 backdrop-blur border-t border-border p-3 z-30"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <Button
          className="w-full h-12 text-base font-semibold"
          disabled={!canBook}
          onClick={handleBook}
          style={
            canBook && brandColor ? { backgroundColor: brandColor } : undefined
          }
        >
          {ctaLabel}
        </Button>
      </div>
    </MobileAppLayout>
  );
};

// Compact coupon row for mobile
interface CouponRowProps {
  userCoupon: AvailableCoupon;
  selectedDate?: Date;
  servicePrice: number;
  isSelected: boolean;
  brandColor?: string;
  onToggle: () => void;
}

const CouponRow = ({
  userCoupon,
  selectedDate,
  servicePrice,
  isSelected,
  brandColor,
  onToggle,
}: CouponRowProps) => {
  const coupon = userCoupon.coupon;
  const isCreditBased =
    coupon.discount_type === "weekly_credits" ||
    coupon.discount_type === "monthly_credits";

  const { data: creditInfo } = useUserCouponCredits(
    isCreditBased ? userCoupon.id : undefined,
    isCreditBased ? selectedDate : undefined
  );

  let hasAvailableCredits = true;
  let creditMessage = "";
  if (isCreditBased) {
    if (creditInfo) {
      hasAvailableCredits = creditInfo.credits_used < creditInfo.credits_available;
      const remaining = creditInfo.credits_available - creditInfo.credits_used;
      creditMessage = hasAvailableCredits
        ? `${remaining} credit${remaining !== 1 ? "s" : ""} left`
        : "No credits left";
    } else {
      const cpp = coupon.credits_per_period || 1;
      creditMessage = `${cpp} credit${cpp !== 1 ? "s" : ""} available`;
    }
  }

  let savingsLabel = "";
  if (isCreditBased) {
    savingsLabel = "Free session";
  } else if (coupon.discount_type === "fixed") {
    savingsLabel = `£${coupon.discount_value} off`;
  } else {
    const amt = (servicePrice * coupon.discount_value) / 100;
    savingsLabel = `${coupon.discount_value}% off (-£${amt.toFixed(2)})`;
  }

  return (
    <Card
      className={`cursor-pointer transition-colors ${
        isSelected ? "border-2" : ""
      } ${!hasAvailableCredits ? "opacity-60" : ""}`}
      style={isSelected && brandColor ? { borderColor: brandColor } : undefined}
      onClick={() => hasAvailableCredits && onToggle()}
    >
      <CardContent className="p-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="text-xs shrink-0">
              {coupon.code}
            </Badge>
            <span className="font-medium text-sm truncate">{coupon.name}</span>
          </div>
          <p className="text-xs text-green-600 font-medium">{savingsLabel}</p>
          {isCreditBased && creditMessage && (
            <p className="text-xs text-muted-foreground mt-0.5">{creditMessage}</p>
          )}
        </div>
        <Button
          size="sm"
          variant={isSelected ? "default" : "outline"}
          disabled={!hasAvailableCredits}
          className="shrink-0"
          style={
            isSelected && brandColor
              ? { backgroundColor: brandColor }
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            if (hasAvailableCredits) onToggle();
          }}
        >
          {!hasAvailableCredits ? "—" : isSelected ? "Applied" : "Apply"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AppBookingFlow;
